import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import Plyr from '../../lib/react-plyr';
import 'plyr/dist/plyr.css';

import ErrorBoundary from '../ErrorBoundary';
import AnnotationWrapper from './View/AnnotationWrapper';
import FileViewPDF from './View/PDF';
import FileViewPDFv3 from './View/PDFv3';
import VideoPlayer from '../VideoPlayer';

import { APIgetFileEmbed } from '../../api/Files';

import {
  getFileExtension,
  isAnnotationableImage,
  pdfViewerV2,
} from '../../helpers/Files';
import { getSetting } from '../../helpers/Settings';

class FileView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      imageViewOpen: false,
      embedCode: null,
      embedUrl: null,
      displayImage: null,
      annotationTip: false,
    };

    this.handleImagePreviewOpen = this.handleImagePreviewOpen.bind(this);
    this.handleImagePreviewClose = this.handleImagePreviewClose.bind(this);
    this.handleDisplayImage = this.handleDisplayImage.bind(this);
    this.handleAnnotationTip = this.handleAnnotationTip.bind(this);
  }

  componentDidMount() {
    this.getEmbed();
    this.handleDisplayImage();
  }

  componentDidUpdate(prevProps) {
    const { file, fileVersion } = this.props;
    const { file: prevFile } = prevProps;

    if (file.uuid !== prevFile.uuid) {
      this.getEmbed();
      this.handleDisplayImage();
    } else if (fileVersion !== prevProps.fileVersion ||
      (file.urls && prevProps.file.urls && file.urls.preview !== prevProps.file.urls.preview)
    ) {
      this.handleDisplayImage();
    }
  }

  handleAnnotationTip(annotationTip) {
    this.setState({ annotationTip });
  }

  handleDisplayImage() {
    const { file } = this.props;

    const fileExtension = getFileExtension(file);
    if (fileExtension === 'PDF') {
      return;
    }

    const expectedUrl = file.urls.preview;
    const imageToLoad = new window.Image();
    imageToLoad.src = expectedUrl;

    imageToLoad.addEventListener('load', () => {
      // Prevent race condition (on editions)
      const { file: fileNew } = this.props;
      if (fileNew.urls.preview === expectedUrl) {
        this.setState({ displayImage: imageToLoad });
      }
    });
  }

  getEmbed() {
    const { file } = this.props;

    if (file.type === 'link') {
      if (file.html_code) {
        const html = `
        <div style="position: relative; height: 0; overflow: hidden; max-width: 100%; padding-bottom: 56.25%;">
          ${file.html_code}
        </div>
        `;
        this.setState({
          embedCode: html,
          embedUrl: null,
        });
      } else {
        APIgetFileEmbed(file.uuid).then((response) => {
          const { data } = response;
          this.setState({
            embedCode: data.embed_code || null,
            embedUrl: data.embed_url || null,
          });
        });
      }
    } else {
      this.setState({
        embedCode: null,
        embedUrl: null,
      });
    }
  }

  handleImagePreviewOpen(e) {
    e.preventDefault();
    const {
      commentsAnnotateMode,
      removeEventListeners,
    } = this.props;
    const { annotationTip } = this.state;

    if (!commentsAnnotateMode && !annotationTip) {
      removeEventListeners();
      this.setState({ imageViewOpen: true });
    }
  }

  handleImagePreviewClose() {
    const { setEventListeners } = this.props;
    setEventListeners();
    this.setState({ imageViewOpen: false });
  }

  render() {
    const {
      file,
      fileVersion,
      sidebarTab,
      commentsAnnotateMode,
      updateCommentsCount,
      handleSidebarTab,
      readOnly,
    } = this.props;
    const {
      imageViewOpen,
      embedCode,
      embedUrl,
      displayImage,
    } = this.state;
    const fileExtension = getFileExtension(file);

    if (file.type_group === 'audio') {
      return (
        <ErrorBoundary>
          <div>
            <div>
              <div>
                <Plyr
                  provider="audio"
                  url={file.urls.audio}
                  title={file.display_name}
                />
              </div>
            </div>
          </div>
        </ErrorBoundary>
      );
    }

    if (file.type_group === 'video') {
      return (
        <ErrorBoundary>
          <VideoPlayer
            file={file}
            fileVersion={fileVersion}
            updateCommentsCount={updateCommentsCount}
            sidebarTab={sidebarTab}
            handleSidebarTab={handleSidebarTab}
            readOnly={readOnly}
          />
        </ErrorBoundary>
      );
    }

    if (fileExtension === 'PDF') {
      if (pdfViewerV2()) {
        return (
          <FileViewPDFv3
            file={file}
            fileVersion={fileVersion}
          />
        );
      }

      return (
        <FileViewPDF
          file={file}
          fileVersion={fileVersion}
          updateCommentsCount={updateCommentsCount}
          handleSidebarTab={handleSidebarTab}
          readOnly={readOnly}
        />
      );
    }

    if (displayImage) {
      if (file.urls.zoom && getSetting('allow_zoom')) {
        return (
          <>
            <div
              role="button"
              tabIndex={0}
              className={classnames({ 'open-image-preview': !commentsAnnotateMode })}
              onClick={this.handleImagePreviewOpen}
              onKeyDown={() => {}}
            >
              {isAnnotationableImage(file) ? (
                <AnnotationWrapper
                  file={file}
                  fileVersion={fileVersion}
                  displayImage={displayImage}
                  handleParentAnnotationTip={this.handleAnnotationTip}
                  type="image"
                  sidebarTab={sidebarTab}
                  updateCommentsCount={updateCommentsCount}
                  handleSidebarTab={handleSidebarTab}
                  readOnly={readOnly}
                />
              ) : (
                <div className="file-image-wrapper file-image-wrapper-basis">
                  <img src={file.urls.preview} alt={file.display_name} />
                </div>
              )}
            </div>
            {imageViewOpen && (
              <Lightbox
                image={file.urls.zoom}
                allowRotate={false}
                doubleClickZoom={2}
                onClose={this.handleImagePreviewClose}
              />
            )}
          </>
        );
      }

      if (isAnnotationableImage(file)) {
        return (
          <AnnotationWrapper
            file={file}
            fileVersion={fileVersion}
            displayImage={displayImage}
            handleParentAnnotationTip={this.handleAnnotationTip}
            type="image"
            sidebarTab={sidebarTab}
            updateCommentsCount={updateCommentsCount}
            handleSidebarTab={handleSidebarTab}
            readOnly={readOnly}
          />
        );
      }
    }

    if (file.type === 'link' && embedCode) {
      return (
        <ErrorBoundary>
          <div
            style={{
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
            }}
          >
            {Parser(embedCode)}
          </div>
        </ErrorBoundary>
      );
    }

    if (file.type === 'link' && embedUrl) {
      return (
        <ErrorBoundary>
          <div
            style={{
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
            }}
          >
            <iframe
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                left: 0,
                top: 0,
                overflow: 'hidden',
              }}
              frameBorder="0"
              type="text/html"
              src={embedUrl}
              width="100%"
              height="100%"
              allowFullScreen
              title={file.display_name}
            />
          </div>
        </ErrorBoundary>
      );
    }

    return (
      <div className="file-image-wrapper file-image-wrapper-basis">
        <img src={file.urls.preview} alt={file.display_name} />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  commentsAnnotateMode: state.Files.commentsAnnotateMode,
});

FileView.propTypes = {
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  sidebarTab: PropTypes.string.isRequired,
  setEventListeners: PropTypes.func.isRequired,
  removeEventListeners: PropTypes.func.isRequired,
  commentsAnnotateMode: PropTypes.bool.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

FileView.defaultProps = {
  fileVersion: null,
  readOnly: false,
};

export default connect(mapStateToProps)(FileView);
